import { css } from "@emotion/react";
import { Home } from "@mui/icons-material";
import { Box, IconButtonProps, styled } from "@mui/material";
import { vec2 } from "gl-matrix";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { EventOrigin, Events, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import IconButtonExt from "components/iconButtonExt";
import { useResetView } from "features/home/useResetView";
import { selectCameraDefaults } from "features/render";
import { useSetController } from "features/render/hooks/useSetController";
import { useToggle } from "hooks/useToggle";
import CursorIcon from "media/icons/cursor.svg?react";
import FlyIcon from "media/icons/fly.svg?react";
import OrbitIcon from "media/icons/orbit.svg?react";
import { degToRad } from "utils/math";

export function NavWheelPrimaryMenu() {
    const [open, toggle] = useToggle(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        function handle(e: PointerEvent) {
            if (!open || !anchorRef.current) {
                return;
            }

            const pos = vec2.fromValues(e.clientX, e.clientY);
            const bbox = anchorRef.current.getBoundingClientRect();
            const origin = vec2.fromValues(bbox.x + bbox.width / 2, bbox.y + bbox.height);
            const distance = vec2.distance(pos, origin);

            if (distance > 100) {
                toggle(false);
            }
        }

        const anchor = anchorRef.current;
        if (anchor) {
            anchor.ownerDocument.addEventListener("pointermove", handle);
            return () => {
                anchor.ownerDocument.removeEventListener("pointermove", handle);
            };
        }
    });

    return (
        <Box
            sx={{
                position: "absolute",
                width: "100%",
                bottom: 0,
                zIndex: 1050,
                display: "flex",
                justifyContent: "center",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                }}
                ref={anchorRef}
            >
                <Backwheel open={open} />
                <Buttons open={open} />
                <Wheel open={open} onOpen={() => toggle(true)} />
            </Box>
        </Box>
    );
}

function Wheel({ onOpen, open }: { onOpen: () => void; open: boolean }) {
    const controller = useAppSelector(selectCameraDefaults).pinhole.controller;
    const [currentController, setCurrentController] = useState(controller);
    const [transitioning, setTransitioning] = useState(false);

    useEffect(() => {
        setTransitioning(true);
        const timeout = setTimeout(() => {
            setCurrentController(controller);
            setTransitioning(false);
        }, 100);
        return () => {
            clearTimeout(timeout);
        };
    }, [controller]);

    let Icon: typeof CursorIcon;
    switch (currentController) {
        case "flight":
            Icon = CursorIcon;
            break;
        case "cadMiddlePan":
            Icon = OrbitIcon;
            break;
        default:
            Icon = FlyIcon;
    }

    return (
        <Box
            sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "79px",
                height: "79px",
                translate: "-50% 50%",
                borderRadius: "50%",
                border: open ? "1px solid #4C85E6" : "1px solid #79A3EC",
                background: open ? "#133D86" : "#1952B3",
                pointerEvents: "auto",
                display: "flex",
                justifyContent: "center",
                paddingTop: "10px",
                transition: "background 0.2s, border 0.2s",
                color: "#D2E0F9",
            }}
            onPointerEnter={onOpen}
        >
            <Box sx={{ transition: "opacity 0.2s", opacity: transitioning ? 0 : 1 }}>
                <Icon />
            </Box>
        </Box>
    );
}

function Backwheel({ open }: { open: boolean }) {
    const size = open ? "131px" : "10px";

    return (
        <Box
            sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: size,
                height: size,
                opacity: 0.5,
                translate: "-50% 50%",
                borderRadius: "50%",
                background: "black",
                transition: "width 0.2s, height 0.2s, translate 0.2s",
            }}
        ></Box>
    );
}

enum TooltipData {
    Home,
    Default,
    Fly,
    Orbit,
}

function tooltipDataFromController(controller: string): TooltipData {
    switch (controller) {
        case "flight":
            return TooltipData.Default;
        case "cadMiddlePan":
            return TooltipData.Orbit;
        default:
            return TooltipData.Fly;
    }
}

function Buttons({ open }: { open: boolean }) {
    const { t } = useTranslation();
    const controller = useAppSelector(selectCameraDefaults).pinhole.controller;
    const setController = useSetController();
    const updateTooltipTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isHovering = useRef(false);

    const [tooltipData, setTooltipData] = useState(tooltipDataFromController(controller));

    useEffect(() => {
        if (updateTooltipTimeout.current) {
            clearTimeout(updateTooltipTimeout.current);
            updateTooltipTimeout.current = null;
        }
    }, []);

    useEffect(() => {
        if (isHovering.current) {
            return;
        }
        setTooltipData(tooltipDataFromController(controller));
    }, [controller]);

    const [title, shortcut] = {
        [TooltipData.Home]: [t("home"), "Cmd/Ctrl+0"],
        [TooltipData.Default]: [t("default"), "v"],
        [TooltipData.Fly]: [t("fly"), "f"],
        [TooltipData.Orbit]: [t("orbit"), "o"],
    }[tooltipData];

    const translations = useMemo(() => {
        const translations: string[] = [];

        const N = 4;
        const offset = open ? 65.5 : 0;
        const step = 180 / N;
        for (let i = 0; i < N; i++) {
            const angle = 180 + step / 2 + step * i;
            const translateX = Math.cos(degToRad(angle)) * offset;
            const translateY = Math.sin(degToRad(angle)) * offset;
            translations.push(`calc(${translateX.toFixed(1)}px - 50%) calc(${translateY.toFixed(1)}px - 50%)`);
        }

        return translations;
    }, [open]);

    const handlePointerEnter = (tooltipData: TooltipData) => {
        setTooltipData(tooltipData);
        isHovering.current = true;
        if (updateTooltipTimeout.current) {
            clearTimeout(updateTooltipTimeout.current);
            updateTooltipTimeout.current = null;
        }
    };

    const handlePointerLeave = () => {
        if (updateTooltipTimeout.current) {
            return;
        }
        isHovering.current = false;
        updateTooltipTimeout.current = setTimeout(() => {
            setTooltipData(tooltipDataFromController(controller));
            updateTooltipTimeout.current = null;
        }, 300);
    };

    return (
        <Box>
            <HomeButton
                sx={{ translate: translations[0] }}
                onPointerEnter={() => handlePointerEnter(TooltipData.Home)}
                onPointerLeave={handlePointerLeave}
                tabIndex={open ? undefined : -1}
            >
                <Home />
            </HomeButton>
            <WheelButton
                sx={{ translate: translations[1] }}
                onClick={() => {
                    setController("flight");
                    void track(Events.toolbar.navigationDefaultTapped, { origin: EventOrigin.Toolbar });
                }}
                active={controller === "flight"}
                onPointerEnter={() => handlePointerEnter(TooltipData.Default)}
                onPointerLeave={handlePointerLeave}
                tabIndex={open ? undefined : -1}
            >
                <CursorIcon />
            </WheelButton>
            <WheelButton
                sx={{ translate: translations[2] }}
                onClick={() => {
                    setController("hover");
                    void track(Events.toolbar.navigationFlyTapped, { origin: EventOrigin.Toolbar });
                }}
                active={controller === "hover"}
                onPointerEnter={() => handlePointerEnter(TooltipData.Fly)}
                onPointerLeave={handlePointerLeave}
                tabIndex={open ? undefined : -1}
            >
                <FlyIcon />
            </WheelButton>
            <WheelButton
                sx={{ translate: translations[3] }}
                onClick={() => {
                    setController("cadMiddlePan");
                    void track(Events.toolbar.navigationOrbitTapped, { origin: EventOrigin.Toolbar });
                }}
                active={controller === "cadMiddlePan"}
                onPointerEnter={() => handlePointerEnter(TooltipData.Orbit)}
                onPointerLeave={handlePointerLeave}
                tabIndex={open ? undefined : -1}
            >
                <OrbitIcon />
            </WheelButton>

            <Tooltip open={open} shortcut={shortcut} label={title} />
        </Box>
    );
}

function HomeButton(props: WheelButtonProps) {
    const [loading, setLoading] = useState(false);
    const resetView = useResetView();

    const handleClick = async () => {
        setLoading(true);
        await resetView();
        setLoading(false);

        void track(Events.toolbar.homeTapped, { origin: EventOrigin.Toolbar });
    };

    return (
        <WheelButton onClick={handleClick} loading={loading} {...props}>
            <Home />
        </WheelButton>
    );
}

function Tooltip({ open, shortcut, label }: { open: boolean; shortcut: string; label: string }) {
    return (
        <Box
            sx={{
                position: "absolute",
                bottom: "100px",
                height: "36px",
                translate: "-50% 0",
                background: "rgba(5, 5, 5, 0.46)",
                padding: "4px 6px",
                borderRadius: "8px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                transition: "opacity 0.2s",
                opacity: open ? 1 : 0,
                color: "white",
                whiteSpace: "nowrap",
            }}
        >
            <Box
                sx={{
                    padding: "0px 6px",
                    background: "#2F3437",
                    borderRadius: "2px",
                }}
            >
                {shortcut}
            </Box>{" "}
            {label}
        </Box>
    );
}

type WheelButtonProps = IconButtonProps & { active?: boolean };

const WheelButton = styled(IconButtonExt)<WheelButtonProps>(
    ({ theme, active }) => css`
        position: absolute;
        transition:
            translate 0.2s,
            background-color 0.2s;
        pointer-events: auto;
        background-color: ${active ? "#1D5CC9" : "#EEF0F1"};
        color: ${active ? theme.palette.primary.contrastText : "#232729"};
        &:hover {
            background-color: ${active ? "#1D5CC9" : "#fff"};
        }
        cursor: ${active ? "default" : "pointer"};
    `,
);
